import React, { useState, useEffect, useCallback } from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import { makeStyles } from 'tss-react/mui';
import 'react-resizable/css/styles.css';
const useStyles = makeStyles()((theme, { opacity, isMinimized, isDragging }) => ({
    modalContainer: {
        position: 'fixed',
        // Width and height will be controlled by Resizable component
        background: `linear-gradient(135deg, rgba(30, 30, 30, ${opacity}) 0%, rgba(45, 45, 45, ${opacity}) 100%)`,
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '2px solid rgba(30, 136, 229, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Disable transitions during drag to prevent ResizeObserver errors
        transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 99999,
        userSelect: 'none',
        '&:hover': {
            boxShadow: '0 12px 48px rgba(11, 108, 227, 0.3)',
            borderColor: 'rgba(30, 136, 229, 0.5)'
        }
    },
    header: {
        padding: '16px 20px',
        background: 'linear-gradient(135deg, rgba(11, 108, 227, 0.2) 0%, rgba(30, 136, 229, 0.15) 100%)',
        borderBottom: isMinimized ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
        cursor: 'move',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        userSelect: 'none',
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(11, 108, 227, 0.3) 0%, rgba(30, 136, 229, 0.2) 100%)'
        }
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1
    },
    dragHandle: {
        fontSize: '20px',
        color: 'rgba(255, 255, 255, 0.6)',
        cursor: 'grab',
        '&:active': {
            cursor: 'grabbing'
        }
    },
    title: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#FFFFFF',
        margin: 0,
        letterSpacing: '0.5px'
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    opacityControl: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    opacityLabel: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)',
        minWidth: '35px',
        textAlign: 'right'
    },
    opacitySlider: {
        width: '80px',
        height: '4px',
        appearance: 'none',
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '2px',
        outline: 'none',
        cursor: 'pointer',
        '&::-webkit-slider-thumb': {
            appearance: 'none',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            background: '#0b6ce3',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#1e88e5',
                transform: 'scale(1.2)'
            }
        },
        '&::-moz-range-thumb': {
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            background: '#0b6ce3',
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#1e88e5',
                transform: 'scale(1.2)'
            }
        }
    },
    button: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: 'rgba(255, 255, 255, 0.1)',
        border: 'none',
        color: '#FFFFFF',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        transition: 'all 0.2s ease',
        '&:hover': {
            background: 'rgba(11, 108, 227, 0.4)',
            transform: 'scale(1.1)'
        },
        '&:active': {
            transform: 'scale(0.95)'
        }
    },
    closeButton: {
        '&:hover': {
            background: 'rgba(244, 67, 54, 0.4)'
        }
    },
    content: {
        flex: 1,
        overflow: 'auto',
        padding: '20px',
        display: isMinimized ? 'none' : 'block',
        '&::-webkit-scrollbar': {
            width: '8px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)'
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(11, 108, 227, 0.4)',
            borderRadius: '4px',
            '&:hover': {
                background: 'rgba(11, 108, 227, 0.6)'
            }
        }
    },
    minimizedContent: {
        padding: '0 20px',
        display: isMinimized ? 'block' : 'none'
    },
    resizeHandle: {
        '& .react-resizable-handle': {
            position: 'absolute',
            width: '20px',
            height: '20px',
        },
        '& .react-resizable-handle-se': {
            bottom: 0,
            right: 0,
            cursor: 'nwse-resize',
            pointerEvents: 'auto',
            '&::after': {
                content: '""',
                position: 'absolute',
                right: '3px',
                bottom: '3px',
                width: '12px',
                height: '12px',
                borderRight: '2px solid rgba(30, 136, 229, 0.6)',
                borderBottom: '2px solid rgba(30, 136, 229, 0.6)',
            }
        },
        '& .react-resizable-handle-e': {
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '10px',
            height: '40px',
            cursor: 'ew-resize',
            borderRight: '2px solid rgba(30, 136, 229, 0.4)',
            pointerEvents: 'auto',
        },
        '& .react-resizable-handle-s': {
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '40px',
            height: '10px',
            cursor: 'ns-resize',
            borderBottom: '2px solid rgba(30, 136, 229, 0.4)',
            pointerEvents: 'auto',
        },
        '& .react-resizable-handle-n': {
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '40px',
            height: '10px',
            cursor: 'ns-resize',
            borderTop: '2px solid rgba(30, 136, 229, 0.4)',
            pointerEvents: 'auto',
        },
        '& .react-resizable-handle-w': {
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '10px',
            height: '40px',
            cursor: 'ew-resize',
            borderLeft: '2px solid rgba(30, 136, 229, 0.4)',
            pointerEvents: 'auto',
        },
        '& .react-resizable-handle-ne': {
            top: 0,
            right: 0,
            cursor: 'nesw-resize',
            pointerEvents: 'auto',
            '&::after': {
                content: '""',
                position: 'absolute',
                right: '3px',
                top: '3px',
                width: '12px',
                height: '12px',
                borderRight: '2px solid rgba(30, 136, 229, 0.6)',
                borderTop: '2px solid rgba(30, 136, 229, 0.6)',
            }
        },
        '& .react-resizable-handle-nw': {
            top: 0,
            left: 0,
            cursor: 'nwse-resize',
            pointerEvents: 'auto',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: '3px',
                top: '3px',
                width: '12px',
                height: '12px',
                borderLeft: '2px solid rgba(30, 136, 229, 0.6)',
                borderTop: '2px solid rgba(30, 136, 229, 0.6)',
            }
        },
        '& .react-resizable-handle-sw': {
            bottom: 0,
            left: 0,
            cursor: 'nesw-resize',
            pointerEvents: 'auto',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: '3px',
                bottom: '3px',
                width: '12px',
                height: '12px',
                borderLeft: '2px solid rgba(30, 136, 229, 0.6)',
                borderBottom: '2px solid rgba(30, 136, 229, 0.6)',
            }
        }
    }
}));
export default function DraggableModal({ title, children, isMinimized = false, opacity = 0.95, onMinimizeToggle, onClose, onOpacityChange, minimizedContent, testId = 'draggable-modal' }) {
    const [isDragging, setIsDragging] = useState(false);
    const { classes, cx } = useStyles({ opacity, isMinimized, isDragging });
    const [position, setPosition] = useState({ x: 20, y: 20 });
    const [size, setSize] = useState({
        width: isMinimized ? 320 : 600,
        height: isMinimized ? 70 : 700
    });
    // Load saved position and size from localStorage with bounds checking
    useEffect(() => {
        const savedPosition = localStorage.getItem(`therapist-dashboard-position`);
        if (savedPosition) {
            try {
                const parsed = JSON.parse(savedPosition);
                // Validate position is within viewport bounds
                const maxX = window.innerWidth - 100;
                const maxY = window.innerHeight - 100;
                const validX = Math.max(0, Math.min(parsed.x, maxX));
                const validY = Math.max(0, Math.min(parsed.y, maxY));
                setPosition({ x: validX, y: validY });
            }
            catch (e) {
                console.error('Failed to load saved position:', e);
            }
        }
        const savedSize = localStorage.getItem(`therapist-dashboard-size`);
        if (savedSize && !isMinimized) {
            try {
                setSize(JSON.parse(savedSize));
            }
            catch (e) {
                console.error('Failed to load saved size:', e);
            }
        }
    }, [isMinimized]);
    // Update size when minimized state changes
    useEffect(() => {
        if (isMinimized) {
            setSize({ width: 320, height: 70 });
        }
        else {
            const savedSize = localStorage.getItem(`therapist-dashboard-size`);
            if (savedSize) {
                try {
                    setSize(JSON.parse(savedSize));
                }
                catch (e) {
                    setSize({ width: 600, height: 700 });
                }
            }
            else {
                setSize({ width: 600, height: 700 });
            }
        }
    }, [isMinimized]);
    // Track drag start to disable transitions
    const handleStart = useCallback(() => {
        setIsDragging(true);
    }, []);
    // Save position to localStorage when dragging stops
    const handleStop = useCallback((_e, data) => {
        setIsDragging(false);
        const newPosition = { x: data.x, y: data.y };
        setPosition(newPosition);
        localStorage.setItem(`therapist-dashboard-position`, JSON.stringify(newPosition));
    }, []);
    const handleOpacityChange = useCallback((e) => {
        const newOpacity = parseFloat(e.target.value);
        if (onOpacityChange) {
            onOpacityChange(newOpacity);
        }
    }, [onOpacityChange]);
    // Handle resize (during drag)
    const handleResize = useCallback((_e, data) => {
        if (!isMinimized) {
            const newSize = { width: data.size.width, height: data.size.height };
            setSize(newSize);
        }
    }, [isMinimized]);
    // Handle resize stop (save to localStorage)
    const handleResizeStop = useCallback((_e, data) => {
        if (!isMinimized) {
            const newSize = { width: data.size.width, height: data.size.height };
            setSize(newSize);
            localStorage.setItem(`therapist-dashboard-size`, JSON.stringify(newSize));
        }
    }, [isMinimized]);
    return (React.createElement(Draggable, { handle: ".drag-handle", cancel: ".react-resizable-handle", position: position, onStart: handleStart, onStop: handleStop },
        React.createElement(Resizable, { width: size.width, height: size.height, onResize: handleResize, onResizeStop: handleResizeStop, minConstraints: [320, isMinimized ? 70 : 400], maxConstraints: [1200, 900], resizeHandles: isMinimized ? [] : ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'], className: classes.resizeHandle },
            React.createElement("div", { className: classes.modalContainer, "data-testid": testId, style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    opacity,
                    width: `${size.width}px`,
                    height: `${size.height}px`
                } },
                React.createElement("div", { className: cx(classes.header, 'drag-handle'), "data-testid": `${testId}-header` },
                    React.createElement("div", { className: classes.headerLeft },
                        React.createElement("span", { className: classes.dragHandle }, "\u2261"),
                        React.createElement("h3", { className: classes.title }, title)),
                    React.createElement("div", { className: classes.controls },
                        React.createElement("div", { className: classes.opacityControl },
                            React.createElement("span", { className: classes.opacityLabel },
                                Math.round(opacity * 100),
                                "%"),
                            React.createElement("input", { type: "range", min: "0.4", max: "1", step: "0.05", value: opacity, onChange: handleOpacityChange, className: classes.opacitySlider, "data-testid": `${testId}-opacity-slider`, "aria-label": "Opacity control" })),
                        onMinimizeToggle && (React.createElement("button", { className: classes.button, onClick: onMinimizeToggle, "data-testid": `${testId}-minimize-btn`, "aria-label": isMinimized ? 'Maximize' : 'Minimize', title: isMinimized ? 'Maximize' : 'Minimize' }, isMinimized ? '+' : '−')),
                        onClose && (React.createElement("button", { className: cx(classes.button, classes.closeButton), onClick: onClose, "data-testid": `${testId}-close-btn`, "aria-label": "Close", title: "Close" }, "\u00D7")))),
                React.createElement("div", { className: classes.content }, children),
                minimizedContent && (React.createElement("div", { className: classes.minimizedContent }, minimizedContent))))));
}
